
import { Component, Emit, Prop, Watch } from 'vue-property-decorator';
import { ColumnHeader } from '@/interfaces/components/ColumnHeader';
import { Page } from '@/interfaces/api/Page';
import { mixins } from 'vue-class-component';
import FormatPriceInObject from '@/mixins/FormatPriceInObject';
import i18n from '@/i18n';
import { VCol, VTextField } from 'vuetify/lib';

@Component
export default class VTable extends mixins(FormatPriceInObject) {
  get actions() {
    return this.editable || this.deleteable || this.customActions;
  }

  get pageCount() {
    /* if (Number.isInteger(this.limit)) {
      this.rowsPerPage = this.limit;
    }*/

    if (this.pagination) {
      return Math.ceil(this.total / this.rowsPerPage);
    }

    return 0;
  }
  @Prop({ type: Array, required: true }) public items!: any;
  @Prop({ type: Array, required: true }) public headers!: ColumnHeader[];
  @Prop({ type: String, default: '' }) public search!: string;
  @Prop({ type: Boolean, default: true }) public pagination!: boolean;
  @Prop({ type: Boolean, default: false }) public selectable!: boolean;
  @Prop({ type: Number, required: false }) public total!: number;
  @Prop({ type: [Function, Boolean], default: true }) public editable!: boolean | ((item: any) => boolean);
  @Prop({ type: [Function, Boolean], default: false }) public deleteable!: boolean | ((item: any) => boolean);
  @Prop({ type: Boolean, default: false }) public customActions!: boolean;
  @Prop({ type: Boolean, default: false }) public loading!: boolean;
  @Prop({ type: Boolean, default: false }) public expandable!: boolean;
  @Prop({ type: Boolean, default: false }) public showPerPagePicker!: boolean;
  @Prop({ type: Array, default: () => [10, 20, 50], required: false }) public itemsPerPageOptions!: number[];
  @Prop({ type: Number, default: () => 10 }) public limit!: number;
  @Prop({ type: Boolean, default: false }) public withoutRouting!: boolean;
  @Prop({ type: Boolean, default: false }) public directRout!: boolean;
  @Prop({ type: String, default: '' }) public directRoutPath!: string;
  @Prop({ type: Object, default: () => ({}) }) public directRoutParams!: { [key: string]: string };
  @Prop({ type: Object, default: () => ({}) }) public directRoutQuery!: { [key: string]: string };
  @Prop({ type: Boolean, default: false }) public jumpTo!: boolean;

  public rowsPerPage: number = 10;

  public page: number = 1;

  public jumpToPage: number | null = null;

  protected selected: any[] = [];

  protected selectedAll: boolean = false;

  protected editDialog: any = {
    isVisible: false,
    value: null,
    name: '',
    original: null,
  };

  @Emit('page')
  @Watch('page')
  public onPageChange(): Page {
    if (this.$route.query.page && parseInt(this.$route.query.page as string, 10) !== this.page) {
      // @ts-ignore
      this.$router.push({ query: { page: this.page, limit: this.rowsPerPage } }).catch((e) => {
        throw Error(`${e}`);
      });
    }

    return { page: this.page, limit: this.rowsPerPage };
  }

  public mounted(): void {
    let page: number = 1;
    let limit: number = this.limit;
    if (this.$route.query.page) {
      page = parseInt(this.$route.query.page as string, 10);
    }
    if (this.$route.query.limit) {
      limit = parseInt(this.$route.query.limit as string, 10);
    }
    if (page !== this.page || limit !== this.rowsPerPage) {
      this.page = page;
      this.rowsPerPage = limit;
      this.$emit('page', { page: this.page, limit: this.rowsPerPage });
    } else {
      if (!this.withoutRouting) {
        // @ts-ignore
        this.$router.push({ query: { page: this.page, limit: this.rowsPerPage } }).catch(() => {
          throw Error('route on VTable');
        });
      }
    }
  }

  public isEditable(item: any) {
    if (typeof this.editable === 'function') {
      return this.editable(item);
    }

    return this.editable;
  }

  public mayDelete(item: any) {
    this.$confirm().then((res: boolean) => {
      if (res) {
        this.$emit('delete', item);
      }
    });
  }

  public resetPagination() {
    this.page = 1;
    this.rowsPerPage = 10;
  }

  public isDeleteable(item: any) {
    if (typeof this.deleteable === 'function') {
      return this.deleteable(item);
    }

    return this.deleteable;
  }

  public isSelected(item: any) {
    return this.selected.indexOf(item) > -1;
  }

  public handleCellClick(header: ColumnHeader, item: any) {
    if (!header.editable || !this.editable) {
      return;
    }
    if (header.value === 'tags') {
      this.$emit('editSpecificCell', { header: header, item });
      return;
    }
    if (header.value === 'taxTakeaway' || header.value === 'taxInside') {
      this.$emit('editTaxTakeaway', { header: header, item });
      return;
    }

    this.editDialog.isVisible = true;
    this.editDialog.name = header.value;
    if (this.editDialog.name === 'name' || this.editDialog.name === 'description') {
      this.editDialog.value = this.$options.filters!.localized(item[header.value]);
    } else if (this.editDialog.name === 'price') {
      this.editDialog.value = item[header.value].$numberDecimal
        ? item[header.value].$numberDecimal
        : item[header.value];
    } else if (this.editDialog.name === 'priceByType.preorder.delivery') {
      this.editDialog.value = item?.priceByType?.preorder?.delivery?.$numberDecimal ?? '';
    } else if (this.editDialog.name === 'priceByType.preorder.takeAway') {
      this.editDialog.value = item?.priceByType?.preorder?.takeAway?.$numberDecimal ?? '';
    } else {
      this.editDialog.value = item[header.value];
    }
    this.editDialog.original = { ...item };
  }

  public handleAsset(item: any, header: any) {
    this.$emit('uploadAsset', {
      venue: item,
      asset: header.value,
    });
  }

  public saveCellValue() {
    const data = {
      original: this.editDialog.original,
      field: this.editDialog.name,
      value: this.editDialog.value,
    };
    if (this.editDialog.name === 'price') {
      // TODO - may mutate unexpected fields
      const arr = ['value'];
      arr.map((type: any) => {
        // mutate merged data
        const res = this.deepSearchItems(data, type, (k: any, v: any) => {
          if (v && v.includes) {
            return v.includes(',');
          }
        });
        res.map((obj: any) => {
          obj[type] = obj[type].replaceAll(',', '.');
        });
      });
    } else if (this.editDialog.name === 'name' || this.editDialog.name === 'description') {
      const original = { ...this.editDialog.original[this.editDialog.name] };
      original[i18n.locale] = this.editDialog.value;
      data.value = original;
    }
    this.$emit('edited', data);
    this.stopEditing();
  }

  public stopEditing() {
    this.editDialog.isVisible = false;
    this.editDialog.value = null;
    this.editDialog.original = null;
    this.editDialog.name = '';
  }

  public showQr(url: string) {
    window.open(url, '_blank');
  }

  public toggleSelect(item: any) {
    if (this.isSelected(item)) {
      const index: number = this.selected.indexOf(item);
      this.selected.splice(index, 1);
    } else {
      this.selected.push(item);
    }
  }

  public toggleAll() {
    if (this.selectedAll) {
      this.selected.push(...this.items);
    } else {
      for (const item of this.items) {
        const index: number = this.selected.indexOf(item);
        if (index > -1) {
          this.selected.splice(index, 1);
        }
      }
    }
  }

  public paramsForDirectRoute(item: any): { [key: string]: string } {
    const params: { [key: string]: string } = {};
    Object.keys(this.directRoutParams).map((key: string) => {
      params[key] = item[this.directRoutParams[key]];
    });
    return params;
  }

  public jumpToPageHandler() {
    if (Number(this.jumpToPage) > 0 && Number(this.jumpToPage) < this.pageCount) {
      this.page = Number(this.jumpToPage);
      this.jumpToPage = null;
    }
  }

  @Watch('selectedAll')
  public onSelectAllToggle() {
    this.toggleAll();
  }

  @Emit('rows')
  @Watch('rowsPerPage')
  public onChangeRowsPerPage() {
    if (this.$route.query.limit && parseInt(this.$route.query.limit as string, 10) !== this.rowsPerPage) {
      this.page = 1;
      // @ts-ignore
      this.$router.push({ query: { page: 1, limit: this.rowsPerPage } });
    }
    this.page = 1;
    return { page: 1, limit: this.rowsPerPage };
  }
}
